/**
 * useCommandPalette Hook
 *
 * Manages command palette state and Cmd+K keyboard shortcut.
 */

import { useState, useCallback, useEffect } from 'react';

interface UseCommandPaletteOptions {
  /** Whether the palette should be enabled */
  enabled?: boolean;
  /** Callback when the palette opens */
  onOpen?: () => void;
  /** Callback when the palette closes */
  onClose?: () => void;
}

interface UseCommandPaletteReturn {
  /** Whether the palette is open */
  isOpen: boolean;
  /** Open the palette */
  open: () => void;
  /** Close the palette */
  close: () => void;
  /** Toggle the palette */
  toggle: () => void;
}

export function useCommandPalette(
  options: UseCommandPaletteOptions = {}
): UseCommandPaletteReturn {
  const { enabled = true, onOpen, onClose } = options;
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    if (!enabled) return;
    setIsOpen(true);
    onOpen?.();
  }, [enabled, onOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Listen for Cmd+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘K (Mac) / Ctrl+K (Win/Linux) opens the palette. Unlike most
      // shortcuts we accept EITHER modifier on either platform: ⌘K is a
      // de-facto cross-platform navigation chord (VS Code, Slack,
      // GitHub, Linear all do this) and users press whichever modifier
      // feels natural. The non-primary modifier on each platform doesn't
      // shadow a native browser/OS chord for K, so the leniency is safe.
      // It also keeps E2E tests platform-agnostic — they can press
      // `Meta+k` regardless of where chromium reports the runner OS.
      const hasPrimaryMod = (e.metaKey || e.ctrlKey) && !(e.metaKey && e.ctrlKey);
      if (hasPrimaryMod && !e.altKey && !e.shiftKey && e.key === 'k') {
        // NOTE: This intentionally diverges from `shouldSkipShortcut` in
        // `@/utils/input`. The command palette is a global
        // navigation shortcut and should open even when the user is typing
        // in a plain <input>, <textarea>, or contenteditable. We only skip
        // when focus is inside a code editor (CodeMirror, Monaco, Lexical)
        // or an element that explicitly opts out of canvas shortcuts.
        //
        // Guard against non-Element targets (Document, when the chip
        // dispatches a synthetic ⌘K) — Document has no .closest().
        const target = e.target instanceof Element ? e.target : null;
        if (
          target?.closest(
            '.cm-editor, .monaco-editor, [data-lexical-editor], [data-no-canvas-shortcuts]'
          )
        ) {
          return;
        }

        // Never hijack the key during IME composition — the browser/IME
        // owns it for cancel/commit. Run this BEFORE preventDefault so
        // composition keystrokes can reach the IME unmodified.
        if (e.isComposing) return;

        // We own Cmd+K — suppress the browser default (Chrome's address
        // bar focus on some platforms) even on held-key repeats.
        e.preventDefault();
        if (e.repeat) return;
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, toggle]);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
}
